import { z } from 'zod'
import { Id, Metres, Vec2 } from './primitives.js'

export const Circle = z.object({
  kind: z.literal('circle'),
  center: Vec2,
  radiusM: Metres,
})

export const Polygon = z.object({
  kind: z.literal('polygon'),
  points: z.array(Vec2).min(3),
})

export const Shape = z.discriminatedUnion('kind', [Circle, Polygon])
export type Shape = z.infer<typeof Shape>

/**
 * A named area. Regions are first-class rather than a property of zone
 * emitters, because a trigger area and a sounding area are not always the same
 * thing: a silent region can start narration elsewhere, and one region can be
 * referenced by several rules.
 */
export const Region = z.object({
  id: Id,
  name: z.string().max(120),
  shape: Shape,
})
export type Region = z.infer<typeof Region>

/**
 * A line the listener can cross, with a direction. `forward` means crossing
 * from the right-hand side of a→b to the left, which is how a start line
 * distinguishes starting the race from wandering back over it.
 */
export const Gate = z.object({
  id: Id,
  name: z.string().max(120),
  a: Vec2,
  b: Vec2,
})
export type Gate = z.infer<typeof Gate>

/**
 * A route through the world, used for distance-along-course triggers. Matching
 * the listener onto a course and firing by chainage is far more robust than
 * proximity circles, because it tolerates the lateral GPS wobble that would
 * otherwise make a runner miss their own cue.
 */
export const Course = z.object({
  id: Id,
  name: z.string().max(120),
  points: z.array(Vec2).min(2),
  /** Cross-track distance beyond which the listener is considered off-course. */
  corridorM: Metres.default(40),
})
export type Course = z.infer<typeof Course>
